/**
 * Configuration du front. Aucune clé, aucun secret : le navigateur n'appelle
 * que `/diagnostic`, jamais le fournisseur de modèle.
 */

function flag(value: string | undefined, parDefaut = false): boolean {
  if (value === undefined) return parDefaut;
  return value === "true" || value === "1";
}

export const CONFIG = {
  /** Point d'entrée du diagnostic. Réécrit par Firebase Hosting en production. */
  diagnosticEndpoint: import.meta.env.VITE_DIAGNOSTIC_ENDPOINT ?? "/diagnostic",
  /**
   * Vrai tant qu'aucun opérateur de paiement réel n'est branché.
   *
   * Le navigateur l'apprenait à l'**étape 3**, après avoir fait choisir un
   * opérateur et saisir un numéro de téléphone. On demandait donc une donnée
   * personnelle pour une transaction qui n'aurait pas lieu, et on le disait
   * après. Même source que le manifeste de déploiement, confrontée à
   * `functions/.env` par `npm run verifier`.
   */
  paiementSimule: flag(import.meta.env.VITE_PAY_SIMULE, true),
  /** Action offerte et titres verrouillés. Appelé après l'affichage du score. */
  actionEndpoint: import.meta.env.VITE_ACTION_ENDPOINT ?? "/action",
  /**
   * Les sept postes du questionnaire dirigeant.
   *
   * L'adresse de l'API est `/entreprise` ; l'écran, lui, vit sous `/dirigeant`.
   * Les deux ne peuvent pas partager un chemin : une réécriture hosting est
   * évaluée **avant** le repli vers index.html, donc une route d'écran portant
   * la même adresse qu'une fonction servirait du JSON à la place de la page.
   * `npm run verifier` refuse le déploiement si les deux se recouvrent.
   */
  entrepriseEndpoint: import.meta.env.VITE_ENTREPRISE_ENDPOINT ?? "/entreprise",
  /** L'action offerte du dirigeant. Appelée après l'affichage du résultat. */
  entreprisePlanEndpoint: import.meta.env.VITE_ENTREPRISE_PLAN_ENDPOINT ?? "/entreprise-plan",
  /** Collecte d'évènements (ticket T9). Réécrit par Firebase Hosting. */
  analyticsEndpoint: import.meta.env.VITE_ANALYTICS_ENDPOINT ?? "/events",
  /**
   * L'identifiant du pixel Meta (T43). Vide : aucun bandeau, aucun script de
   * Meta. Ce n'est pas un secret — il est lisible dans toute page qui charge
   * un pixel ; le jeton de l'API Conversions, lui, ne quitte pas le serveur.
   */
  metaPixelId: (import.meta.env.VITE_META_PIXEL_ID ?? "").trim(),
  /** Résultats anonymes et percentiles réels (ticket T12). */
  resultsEndpoint: import.meta.env.VITE_RESULTS_ENDPOINT ?? "/results",
  /** État du parrainage d'une session (ticket T15). */
  referralEndpoint: import.meta.env.VITE_REFERRAL_ENDPOINT ?? "/referral",
  /**
   * Émission et vérification d'une attestation (ticket T13). Sous `/api/`
   * depuis que `/attestation` est une page — la porte de saisie d'un numéro.
   */
  attestationEndpoint: import.meta.env.VITE_ATTESTATION_ENDPOINT ?? "/api/attestation",
  /** La page de progression à quatre-vingt-dix jours (28/09/2026). */
  progressionEndpoint: import.meta.env.VITE_PROGRESSION_ENDPOINT ?? "/api/progression",
  /**
   * Les outils du moment, datés. Sous `/api/` parce que c'est de la donnée ;
   * la page qui l'affiche vit à `/outils`, comme les autres écrans.
   */
  outilsEndpoint: import.meta.env.VITE_OUTILS_ENDPOINT ?? "/api/outils",
  /** Derniers diagnostics, pour le bandeau d'activité (ticket T14). */
  activiteEndpoint: import.meta.env.VITE_ACTIVITE_ENDPOINT ?? "/activite",

  /** Le repère du dirigeant : ce que déclarent les autres de son secteur. */
  repereEndpoint: import.meta.env.VITE_REPERE_ENDPOINT ?? "/repere",
  /**
   * Les interrupteurs du produit, posés depuis `/pilotage`.
   *
   * Lu une fois au démarrage. Il ne porte aucun secret : seulement de quoi
   * savoir si un écran doit s'afficher.
   */
  reglagesEndpoint: import.meta.env.VITE_REGLAGES_ENDPOINT ?? "/api/reglages",

  /*
    Les données sous `/api/`, les pages sans.

    `/barometre` et `/pilotage` sont des **pages**, servies par le routeur du
    front. Tant que la donnée portait la même adresse, la réécriture
    d'hébergement gagnait — elle est évaluée avant le repli vers `index.html` —
    et le navigateur recevait du JSON là où l'on attendait un écran. Le préfixe
    sépare les deux pour de bon.
  */

  /** Les chiffres publiés du baromètre. Lecture seule, sans identifiant. */
  barometreEndpoint: import.meta.env.VITE_BAROMETRE_ENDPOINT ?? "/api/barometre",

  /** Tableau de bord interne. Fermé par mot de passe : volumes, recette, coût. */
  pilotageEndpoint: import.meta.env.VITE_PILOTAGE_ENDPOINT ?? "/api/pilotage",
  /** Ouverture d'un paiement (ticket T5). */
  paymentEndpoint: import.meta.env.VITE_PAYMENT_ENDPOINT ?? "/pay/init",
  /** Consultation d'un paiement. N'en déclenche jamais la délivrance. */
  payStatusEndpoint: import.meta.env.VITE_PAY_STATUS_ENDPOINT ?? "/pay/status",
  /** Simulation d'opérateur. Répond 404 hors environnement de test. */
  paySimulerEndpoint: import.meta.env.VITE_PAY_SIMULER_ENDPOINT ?? "/pay/simuler",
  payRelancerEndpoint: import.meta.env.VITE_PAY_RELANCER_ENDPOINT ?? "/pay/relancer",
  /** Demande de remboursement. Enregistre et date ; ne rembourse pas. */
  payRemboursementEndpoint:
    import.meta.env.VITE_PAY_REMBOURSEMENT_ENDPOINT ?? "/pay/remboursement",
  /**
   * Données d'exemple : bandeau d'activité et panneau technique.
   * Doit rester à `false` en production (règle non négociable n° 6).
   */
  demoData: flag(import.meta.env.VITE_DEMO_DATA, false),
  /**
   * Livraison par WhatsApp activée.
   *
   * **Par défaut faux.** Tant que le canal n'est pas configuré, l'interface ne
   * doit ni réclamer un numéro, ni recueillir un consentement, ni annoncer un
   * envoi : promettre une livraison qui n'aura pas lieu est un faux signal, et
   * collecter une donnée personnelle qu'on n'utilisera pas est une collecte
   * sans finalité. Le drapeau suit la variable WHATSAPP_PHONE_ID du dépôt,
   * celle-là même que la vérification confronte au manifeste de déploiement.
   */
  messagerieActive: flag(import.meta.env.VITE_MESSAGERIE, false),
  /**
   * L'adresse à laquelle on nous écrit — remboursement, droits sur les
   * données, question.
   *
   * Écrite ici plutôt que dans trois écrans. La page de vente promettait
   * « écrivez-nous sur WhatsApp » alors que le canal WhatsApp n'est pas
   * ouvert : **la seule voie de recours nommée à l'acheteur n'existait pas**,
   * et elle était nommée sous la garantie de remboursement, à l'endroit où on
   * demande 3 000 F. Les mentions légales, elles, donnaient déjà la bonne
   * adresse — mais repliée, trois écrans plus bas.
   *
   * **Corrigé le 24/08 : `.io` et non `.com`.** Le domaine de RISEAFRICA est
   * `riseafrica.io` ; l'adresse publiée était en `.com`, c'est-à-dire sur un
   * domaine que l'éditeur ne tient pas. Elle figurait aux trois endroits qui
   * comptent le plus : l'exercice des droits sur les données, la demande de
   * remboursement, et l'engagement pris en attendant l'immatriculation. Une
   * voie de recours qui n'aboutit pas est pire qu'une absence de voie — elle
   * fait croire à quelqu'un qu'il a écrit.
   */
  contactEmail: "hello@riseafrica.io",
  /**
   * L'accompagnement Propulse est-il réellement assuré ?
   *
   * Faux par défaut, et la formule à 9 000 F n'est alors pas proposée. Elle
   * livrait exactement le rapport à 3 000 F — même document, aucun service —
   * et le serveur refuse désormais ce plan tant qu'il n'est pas déclaré
   * assuré. Afficher une carte dont le paiement se solderait par un refus
   * serait pire que de ne pas l'afficher.
   */
  accompagnementActif: flag(import.meta.env.VITE_ACCOMPAGNEMENT, false),
  // Prix unique, pour tout le monde. Le parrainage ne réduit pas le prix et
  // n'ouvre aucun supplément : celui qui était annoncé — « un second diagnostic
  // à 90 jours, offert » — est déjà gratuit sans condition, le diagnostic étant
  // librement répétable. Le compteur de filleuls reste, il dit un fait.
  price: 3000,
  priceBundle: 9000,
  // `launchLimit` a été retiré : il affichait « les 500 premiers rapports » sans
  // que rien ne compte ni ne décrémente. Une rareté annoncée sans mécanisme est
  // un compteur inventé, ce que la règle non négociable n° 6 interdit.
  cvMaxChars: 6000,
} as const;
